import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ListChecks, Book, Download, Save, Loader2, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import html2pdf from 'html2pdf.js';
import { FlashcardSet } from '@/lib/types/flashcard';
import { FlashcardDisplay } from '@/components/flashcard/flashcard-display';
import { toast } from 'sonner';

interface Chapter {
    title: string;
    content: string;
}

interface BookData {
    title: string;
    chapters: Chapter[];
    videoInfo: any;
}

interface MCQQuestion {
    question: string;
    options: {
        A: string;
        B: string;
        C: string;
        D: string;
    };
    correct_answer: 'A' | 'B' | 'C' | 'D';
    explanation: string;
}

interface OutputDisplayProps {
    mcqData?: { questions: MCQQuestion[] };
    glossaryData?: any;
    bookData?: BookData;
    flashcardData?: FlashcardSet;
}

export function OutputDisplay({ mcqData, glossaryData, bookData, flashcardData }: OutputDisplayProps) {
    const [activeTab, setActiveTab] = useState<'mcq' | 'glossary' | 'book' | 'flashcards'>(() => {
        if (mcqData) return 'mcq';
        if (glossaryData) return 'glossary';
        if (bookData) return 'book';
        if (flashcardData) return 'flashcards';
        return 'mcq';
    });
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        console.log('OutputDisplay: Active tab changed to:', activeTab);
    }, [activeTab]);

    const handlePdfDownload = async () => {
        setIsDownloading(true);
        try {
            const element = document.getElementById('content-for-pdf');
            if (!element) return;
            
            const opt = {
                margin: 1,
                filename: `${activeTab === 'mcq' ? 'quiz' : activeTab}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
            };

            await html2pdf().set(opt).from(element).save();
            toast.success('PDF downloaded successfully!');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download PDF');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setActiveTab('mcq')}
                        className={cn(
                            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                            {
                                'bg-primary-50 text-primary-600': activeTab === 'mcq',
                                'text-gray-600 hover:bg-gray-50': activeTab !== 'mcq',
                            }
                        )}
                    >
                        <ListChecks className="h-4 w-4" />
                        MCQ Quiz
                    </button>
                    <button
                        onClick={() => setActiveTab('glossary')}
                        className={cn(
                            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                            {
                                'bg-primary-50 text-primary-600': activeTab === 'glossary',
                                'text-gray-600 hover:bg-gray-50': activeTab !== 'glossary',
                            }
                        )}
                    >
                        <Book className="h-4 w-4" />
                        Glossary
                    </button>
                    <button
                        onClick={() => setActiveTab('book')}
                        className={cn(
                            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                            {
                                'bg-primary-50 text-primary-600': activeTab === 'book',
                                'text-gray-600 hover:bg-gray-50': activeTab !== 'book',
                            }
                        )}
                    >
                        <FileText className="h-4 w-4" />
                        Smart Notes
                    </button>
                    {flashcardData && (
                        <button
                            onClick={() => setActiveTab('flashcards')}
                            className={cn(
                                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                                {
                                    'bg-primary-50 text-primary-600': activeTab === 'flashcards',
                                    'text-gray-600 hover:bg-gray-50': activeTab !== 'flashcards',
                                }
                            )}
                        >
                            <Square className="h-4 w-4" />
                            Flashcards
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={handlePdfDownload}
                        disabled={isDownloading || activeTab === 'flashcards'}
                        variant="outline"
                        className="gap-2"
                    >
                        {isDownloading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4" />
                        )}
                        Download PDF
                    </Button>
                </div>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                    className="rounded-lg border border-gray-200 bg-white p-6"
                >
                    <div id="content-for-pdf">
                        {activeTab === 'mcq' && mcqData && (
                            <div className="space-y-6">
                                {mcqData.questions.map((question, index) => (
                                    <div key={index} className="rounded-lg border border-gray-200 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            {question.question}
                                        </h3>
                                        <div className="mt-4 space-y-2">
                                            {Object.entries(question.options).map(([key, value]) => (
                                                <label key={key} className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name={`question-${index}`}
                                                        className="text-primary-600"
                                                    />
                                                    <span className="text-gray-700">{value}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="mt-4 rounded-lg bg-gray-50 p-4">
                                            <p className="font-medium text-gray-900">Explanation:</p>
                                            <p className="mt-1 text-gray-700">{question.explanation}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {activeTab === 'glossary' && glossaryData && (
                            <div className="space-y-6">
                                {glossaryData.content.map((item: any, index: number) => (
                                    <div key={index} className="rounded-lg border border-gray-200 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900">{item.term}</h3>
                                        <p className="mt-2 text-gray-700">{item.definition}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {activeTab === 'book' && bookData && (
                            <div className="prose prose-indigo max-w-none">
                                <h1 className="text-3xl font-bold text-gray-900">{bookData.title}</h1>
                                
                                {/* Table of Contents */}
                                <div className="my-8 rounded-lg bg-gray-50 p-6">
                                    <h2 className="mb-4 text-xl font-semibold text-gray-900">Table of Contents</h2>
                                    <div className="space-y-2">
                                        {bookData.chapters.map((chapter, index) => (
                                            <div 
                                                key={index}
                                                className="group flex cursor-pointer items-center"
                                                onClick={() => {
                                                    const element = document.getElementById(`chapter-${index + 1}`);
                                                    if (element) {
                                                        element.scrollIntoView({ behavior: 'smooth' });
                                                    }
                                                }}
                                            >
                                                <span className="mr-2 text-gray-400">{index + 1}.</span>
                                                <span className="text-gray-700 transition-colors hover:text-primary-600">
                                                    {chapter.title}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Chapters */}
                                <div className="mt-12 space-y-12">
                                    {bookData.chapters.map((chapter, index) => {
                                        const contentWithoutTitle = chapter.content.replace(
                                            new RegExp(`^${chapter.title}\\s*`), 
                                            ''
                                        );
                                        
                                        return (
                                            <section 
                                                key={index} 
                                                id={`chapter-${index + 1}`}
                                                className="scroll-mt-6 break-before-page"
                                            >
                                                <h2 className="mb-2 text-2xl font-semibold text-gray-900">
                                                    Chapter {index + 1}
                                                </h2>
                                                <h3 className="mb-6 text-xl font-medium text-gray-800">
                                                    {chapter.title}
                                                </h3>
                                                <div 
                                                    className="prose prose-lg prose-indigo"
                                                    dangerouslySetInnerHTML={{ __html: contentWithoutTitle }} 
                                                />
                                            </section>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {activeTab === 'flashcards' && flashcardData && (
                            <FlashcardDisplay data={flashcardData} />
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
} 